import React, { Component } from 'react';
import { Modal, Button, ProgressBar } from 'react-bootstrap';

import SystemUtils from '../SystemUtils';

import './RecordingDownloadPopup.css';

//status poll period: Pipeline.js polls pipeline_active at 1 s; a progress bar wants a little
//more, and the server side watchdog only needs a poll within 3 min to keep the job alive
export const POLL_INTERVAL_MS = 500;

//a poll may fail transiently (a dropped connection, a momentary 5xx); the server tolerates 3
//minutes of silence before it reaps the job, so keep retrying rather than failing on the first
//miss. 20 misses x 500 ms = 10 s, well inside that window.
export const MAX_POLL_FAILURES = 20;

//zipping never reads 100%: the last step is the archive being handed to the browser, which
//the 'Downloading' phase shows. 0 when the server has not sized the recording yet.
export const zipPercent = (zippedBytes, totalBytes) => {
    return (totalBytes > 0) ? Math.min(99, Math.floor(100 * zippedBytes / totalBytes)) : 0;
};

export const downloadPercent = (sentBytes, archiveBytes) => {
    return (archiveBytes > 0) ? Math.floor(100 * sentBytes / archiveBytes) : 0;
};

//job states in which the popup is still waiting on the server; the footer button reads
//'Cancel' in these and 'Close' everywhere else
const ACTIVE_STATES = ['starting', 'zipping', 'ready', 'downloading'];

//the status servlet sends every number as a decimal string
const toNumber = (value) => {
    const n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
};

//Progress popup behind the 'Download zipped recording' button on Recording Info.
//show(recording) asks the server to tar.gz the recording folder (recording_zip_start), polls
//recording_zip_status until the archive is ready, then hands the download to the browser via
//a hidden <a download> click on recording_zip_download (multi-GB files rule out fetch+blob)
//and keeps polling so the bar follows the bytes the server has streamed. Closing before the
//job is over cancels it on the server: tar is killed and/or the archive deleted.
class RecordingDownloadPopup extends Component {

    constructor(props) {
        super(props);

        this.state = {
            showPopup: false,
            recording: '',
            jobState: '', //'' | starting | zipping | ready | downloading | done | failed | cancelled
            percent: 0,
            label: '',
            subText: '',
            statusText: ''
        };

        //job bookkeeping lives on the instance: the close handler needs it synchronously and
        //none of it affects rendering on its own
        this.jobId = '';
        this.jobFinished = false; //server reported a terminal state, so there is nothing to cancel
        this.downloadStarted = false; //the hidden anchor is clicked exactly once per job
        this.pollTimer = null;
        this.pollFailures = 0; //consecutive failed status polls; reset on any good reply
        this.warnText = ''; //a non-fatal warning from the server (e.g. tar read some files while they changed)

        //invalidates in-flight requests: a start/status response only applies while its token
        //is current, so a slow reply cannot touch a closed or reused popup
        this.generation = 0;
    }

    componentWillUnmount() {
        this.abandonJob();
    }

    show = (recording) => {
        //defensive: a job from an earlier show() that was never closed is cancelled first
        this.abandonJob();

        this.generation++;
        const token = this.generation;

        this.setState({
            showPopup: true,
            recording: recording,
            jobState: 'starting',
            percent: 0,
            label: 'Starting',
            subText: 'Asking the server to zip ' + recording,
            statusText: ''
        });

        fetch('recording_zip_start', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: 'recording=' + encodeURIComponent(recording)
        })
        .then((res) => {
            if (!res.ok)
            {
                throw new Error('HTTP ' + res.status + ' - is the zip download service deployed on this server?');
            }

            return res.json();
        })
        .then((data) => {
            const info = (data && data[0]) ? data[0] : {};

            if (token !== this.generation)
            {
                //closed while the job was starting: do not leave it zipping on the server
                if (info.jobId)
                {
                    this.sendCancel(info.jobId);
                }

                return;
            }

            if ('true' !== String(info.success) || !info.jobId)
            {
                throw new Error(info.msg ? info.msg : 'the server did not start the zip job');
            }

            this.jobId = info.jobId;
            this.pollStatus(token);
        })
        .catch((err) => {
            if (token === this.generation)
            {
                this.fail(err.message);
            }
        })
    }

    //one status request at a time: the next poll is only scheduled once this one has answered,
    //so a slow server never piles requests up
    pollStatus = (token) => {

        fetch('recording_zip_status', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: 'jobId=' + encodeURIComponent(this.jobId)
        })
        .then((res) => {
            if (!res.ok)
            {
                throw new Error('HTTP ' + res.status + ' - the zip job status could not be read');
            }

            return res.json();
        })
        .then((data) => {
            if (token !== this.generation)
            {
                return;
            }

            this.pollFailures = 0;
            this.applyStatus((data && data[0]) ? data[0] : {});

            if (!this.jobFinished)
            {
                this.schedulePoll(token);
            }
        })
        .catch((err) => {
            if (token !== this.generation)
            {
                return;
            }

            this.pollFailures++;

            //a single dropped poll must not fail a healthy job: keep retrying until the server's
            //own watchdog would have reaped it anyway
            if (this.pollFailures >= MAX_POLL_FAILURES)
            {
                this.fail(err.message);
            }
            else
            {
                this.schedulePoll(token);
            }
        })
    }

    schedulePoll = (token) => {
        this.pollTimer = setTimeout(() => {
            this.pollTimer = null;

            if (token === this.generation)
            {
                this.pollStatus(token);
            }
        }, POLL_INTERVAL_MS);
    }

    clearPollTimer = () => {
        if (null !== this.pollTimer)
        {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    applyStatus = (status) => {
        const archiveName = this.state.recording + '.tar.gz';

        //the server clears msg on the terminal 'done' frame, so remember the last non-empty one
        //(a tar warning, e.g. a file changed while being read) to keep showing it when complete
        if (status.msg && ('ready' === status.state || 'downloading' === status.state))
        {
            this.warnText = status.msg;
        }

        if ('zipping' === status.state)
        {
            const zipped = toNumber(status.zippedBytes);
            const total = toNumber(status.totalBytes);
            const pct = zipPercent(zipped, total);

            this.setState({
                jobState: 'zipping',
                percent: pct,
                label: 'Zipping ' + pct + '% | ' + SystemUtils.GetSizeString(zipped) + ' of ' + SystemUtils.GetSizeString(total),
                subText: 'Creating ' + archiveName + ' on the server',
                statusText: ''
            });
        }
        else if ('ready' === status.state || 'downloading' === status.state)
        {
            //'ready' may be reported a few times until the browser's GET flips the job to
            //'downloading'; the anchor is clicked on the first one only
            if ('ready' === status.state && !this.downloadStarted)
            {
                this.downloadStarted = true;
                this.startBrowserDownload(this.jobId, archiveName);
            }

            const sent = toNumber(status.sentBytes);
            const archiveBytes = toNumber(status.archiveBytes);
            const pct = downloadPercent(sent, archiveBytes);

            this.setState({
                jobState: 'downloading',
                percent: pct,
                label: 'Downloading ' + pct + '% | ' + SystemUtils.GetSizeString(sent) + ' of ' + SystemUtils.GetSizeString(archiveBytes),
                subText: 'Your browser is saving ' + archiveName,
                statusText: ''
            });
        }
        else if ('done' === status.state)
        {
            this.jobFinished = true;

            this.setState({
                jobState: 'done',
                percent: 100,
                label: 'Download complete',
                subText: archiveName + ' (' + SystemUtils.GetSizeString(toNumber(status.archiveBytes)) + ') was downloaded and has been removed from the server',
                statusText: ''
            });
        }
        else if ('failed' === status.state || 'cancelled' === status.state)
        {
            this.jobFinished = true;

            this.setState({
                jobState: status.state,
                label: ('failed' === status.state) ? 'Failed' : 'Cancelled',
                subText: '',
                statusText: status.msg ? status.msg : ''
            });
        }
        //any other state: keep what is on screen and poll again
    }

    //same hidden-anchor pattern as RecordingsList.handleDownload: the browser owns the download
    //(its own progress UI, no memory cost), the servlet counts the bytes it streams
    startBrowserDownload = (jobId, archiveName) => {
        var element = document.createElement('a');
        element.setAttribute('href', 'recording_zip_download?jobId=' + encodeURIComponent(jobId));
        element.setAttribute('download', archiveName);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }

    //a request failed on this side: the job may still be running on the server, so jobFinished
    //stays false and closing the popup still sends the cancel
    fail = (message) => {
        this.clearPollTimer();

        this.setState({
            jobState: 'failed',
            label: 'Failed',
            subText: '',
            statusText: message
        });
    }

    //fire-and-forget: the popup is on its way out, there is nothing to show for the outcome
    sendCancel = (jobId) => {

        fetch('recording_zip_cancel', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: 'jobId=' + encodeURIComponent(jobId)
        })
        .catch(console.log)
    }

    //stops polling, cancels an unfinished job on the server and forgets it; shared by close and
    //unmount, so it must not touch state
    abandonJob = () => {
        this.generation++; //any in-flight start/status response is now stale
        this.clearPollTimer();

        if (this.jobId.length > 0 && !this.jobFinished)
        {
            this.sendCancel(this.jobId);
        }

        this.jobId = '';
        this.jobFinished = false;
        this.downloadStarted = false;
        this.pollFailures = 0;
        this.warnText = '';
    }

    handleClose = () => {
        this.abandonJob();

        this.setState({ showPopup: false });
    }

    render() {
        const active = ACTIVE_STATES.indexOf(this.state.jobState) >= 0;

        return (
            <Modal
              className="recordingDownloadRoot"
              backdropClassName="recordingDownloadBackdrop"
              dialogClassName="recordingDownloadModal"
              show={this.state.showPopup}
              onHide={this.handleClose}
              backdrop="static"
              animation={false}
              centered
            >
            <Modal.Header closeButton>
                <Modal.Title><h3>Download zipped recording</h3></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="recordingDownloadName">{this.state.recording}</div>
                <div className="recordingDownloadBarWrap">
                    <ProgressBar className="recordingDownloadBar" animated={active} now={this.state.percent} />
                    <div className="recordingDownloadBarLabel">{this.state.label}</div>
                </div>
                <div className="recordingDownloadSubText">{this.state.subText}</div>
                {this.warnText.length > 0 && 'failed' !== this.state.jobState && 'cancelled' !== this.state.jobState &&
                    <div className="recordingDownloadWarnText">Warning: {this.warnText}</div>
                }
                {this.state.statusText.length > 0 &&
                    <div className="recordingDownloadStatusText">{this.state.statusText}</div>
                }
            </Modal.Body>
            <Modal.Footer>
                <Button size="lg" onClick={this.handleClose}>{active ? 'Cancel' : 'Close'}</Button>
            </Modal.Footer>
            </Modal>
        )
    }
}

export default RecordingDownloadPopup;
